import {
  Dispatch,
  SetStateAction,
  useCallback,
  useMemo,
  useState,
} from "react";
import {
  addMonths,
  format,
  isPast,
  isFuture,
  isSameDay,
  isSameMonth,
  isToday,
  startOfMonth,
  subMonths,
} from "date-fns";

import { cn } from "@/utils";
import { Button } from "@/components";
import { useTranslate } from "@/providers/context";

import { getCalendarDates, getWeekHeaders } from "../util.tsx";
import { MonthSelector } from "./MonthSelector.tsx";
import { useCalendarData } from "../hooks/useCalendarData.tsx";

interface MiniCalendarProps {
  selectedDate: Date;
  setSelectedDate: Dispatch<SetStateAction<Date>>;
  isCompletePreviously?: boolean;
}

export const MiniCalendar = ({
  selectedDate,
  setSelectedDate,
  isCompletePreviously,
}: MiniCalendarProps) => {
  const currentDate = new Date();
  const { getEventsForDate, getEventIcon } = useCalendarData();
  const { translate } = useTranslate();

  const [visibleMonth, setVisibleMonth] = useState<Date>(
    startOfMonth(selectedDate),
  );

  const calendarDates = useMemo(() => {
    return getCalendarDates(visibleMonth);
  }, [visibleMonth]);

  const handleDateChange = useCallback(
    (d: Date) => {
      setSelectedDate((dt) => {
        const _date = new Date(dt);
        _date.setFullYear(d.getFullYear(), d.getMonth(), d.getDate());
        return _date;
      });
    },
    [setSelectedDate],
  );

  const setToday = () => {
    handleDateChange(currentDate);
    setVisibleMonth(startOfMonth(currentDate));
  };

  const groupedByColumn = useMemo(() => {
    return getWeekHeaders().map((header, idx) => {
      // Build up the Nodes in each column
      // Start with the header eg MON
      const column = [
        <div
          key={header}
          className={cn("caption text-item-secondary mb-2 w-full")}
        >
          {header}
        </div>,
      ];
      // Add the dates for that day of the week
      for (let i = idx; i < calendarDates.length; i += 7) {
        const d = calendarDates[i];
        const selected = isSameDay(selectedDate, d);
        const date = format(d, "dd");
        const today = isToday(d);
        const disabled =
          (isCompletePreviously ? isFuture(d) : isPast(d)) && !today;
        const hidden = !isSameMonth(visibleMonth, d);
        column.push(
          <div
            key={d.toDateString()}
            onClick={() => {
              if (hidden || disabled) return;
              handleDateChange(d);
            }}
            className={cn(
              "text-item-primary mb-3 flex h-[34px] flex-col items-center  justify-center text-center",
              disabled ? "text-item-disabled" : "cursor-pointer",
              today ? "p2-extrabold" : "p2-medium",
              hidden && "opacity-0",
            )}
          >
            <div
              className={cn(
                "mb-0.5 flex h-[28px] w-[28px] items-center justify-center rounded-full",
                selected && "bg-highlightPrimary text-white",
              )}
            >
              {date}
            </div>
            <div className="flex h-[4px] w-full justify-center gap-1 text-opacity-0">
              {getEventsForDate(d)
                ?.filter((e) => !e.isClientScheduled)
                ?.slice(0, 4)
                ?.map((e) =>
                  getEventIcon(e, { size: "small", colorOnly: true }),
                )}
            </div>
          </div>,
        );
      }
      return column;
    });
  }, [
    calendarDates,
    getEventIcon,
    getEventsForDate,
    handleDateChange,
    selectedDate,
    visibleMonth,
    isCompletePreviously,
  ]);

  return (
    <div data-testid="mini-calendar">
      <div className="mb-4 flex items-center justify-between">
        <MonthSelector
          month={visibleMonth}
          setMonth={(month) => setVisibleMonth(month)}
          isMaxMonth={() =>
            isCompletePreviously
              ? isSameMonth(currentDate, visibleMonth)
              : isSameMonth(addMonths(currentDate, 3), visibleMonth)
          }
          isMinMonth={() =>
            isCompletePreviously
              ? isSameMonth(subMonths(currentDate, 6), visibleMonth)
              : isSameMonth(currentDate, visibleMonth)
          }
          className="p2-bold"
        />
        <Button
          variant="secondary"
          size="s"
          className="caption"
          onClick={(e) => {
            e.stopPropagation();
            e.preventDefault();
            setToday();
          }}
        >
          {translate("Today")}
        </Button>
      </div>
      <div className="flex justify-between">
        {groupedByColumn.map((items, idx) => {
          return <div key={`column-${idx}`}>{items}</div>;
        })}
      </div>
    </div>
  );
};
